/**
 * 创建页「五来源」的**单点配置**(波1,2026-07-25)—— 统一创建要收敛的就是这里。
 *
 * ═══ 收敛前的样子 ═══════════════════════════════════════════════════════════════
 * 五个来源(自由 / 目的卡 / 追爆蓝图 / 模板 / 海报转视频)共用一个 1664 行的外壳,
 * 谁显谁不显靠一层层手写否定式 if 互斥:
 *     {!fromPoster && !templateId && !(panelScenes.length >= 1) && <VideoBriefPanel/>}
 *     {!templateId && !fromPoster && <ProductImagePanel/>}
 *     {panelScenes >= 1 ? <Grid/> : templateId || fromPoster ? null : <文案框/>}
 *     {!fromPoster && !tpl && <保存草稿/>}
 * 每加一个来源,**每一个** if 都要补一个 `&& !newSource`;漏补一处的表现不是报错,
 * 而是两个来源的面板同时出现(或该出的没出)—— 只有真跑那条来源才看得见。
 *
 * ═══ 收敛成什么 ═════════════════════════════════════════════════════════════════
 * 一个纯函数把「我从哪来」判一次,产出这份 descriptor;页面各处改成**正面读字段**
 * (`ui.showBrief` 而不是 `!a && !b && !c`)。新增来源 = 在本文件加一档 + 在这里
 * 决定它的 5 个开关,页面不用改。
 *
 * ★行为等价,不是重构顺手改交互:每个开关都逐字对着波1前的那串 if 写,
 * `videoSource.test.ts` 用真值表逐档锁死(五来源 × 出分镜前后)。
 *
 * ★与 `FormState.source` 的关系:`source`(FREE/TEMPLATE/FROM_POSTER)是**后端契约字段**
 * (随 compose 请求上送、进草稿),只有三值;本 descriptor 是**前端 UI 维度**,要区分
 * 「自由 / 目的卡 / 追爆蓝图」这三种都上送 `FREE` 的来源。两者不是一回事,别合并。
 */
import type { BriefPurposeDef } from "@/entities/scene-card/model/briefPurpose";

/** UI 维度的来源档(≠ 后端 `FormState.source` 三值)。 */
export type VideoSourceKind = "free" | "purpose" | "explosive" | "template" | "poster";

export interface VideoSourceInput {
  /** `?fromPoster=` 有值 = 海报转视频流。 */
  fromPoster: boolean;
  /** `?templateId=` 有值 = 模板流(注意:模板详情 `tpl` 是异步拉的,面板显隐必须看 id 不看 tpl)。 */
  templateId: boolean;
  /** 追爆蓝图已装载(`form.explosiveFormulaName` + payload 解出来的那份)。 */
  hasBlueprint: boolean;
  /** 目的卡带来的目的(`scenePreset{kind:"video"}` 消费后落在这里)。 */
  purpose: BriefPurposeDef | null;
  /** 已编排出分镜(`form.panelScenes.length >= 1`)—— 出分镜后创建区让位给导演台。 */
  hasStoryboard: boolean;
  /**
   * 追爆蓝图当前档位(BPWB-1,2026-09-03);无蓝图时无意义,传 null。
   * 仿拍档的时长由原片镜头结构决定(ai `estimate_copy_duration` 同源),格式行摆着就是
   * 「选45出15」同族投诉的温床 —— 它要参与 showFormat 的判定。
   */
  fidelityMode: "learn" | "copy" | null;
}

export interface VideoSourceUi {
  kind: VideoSourceKind;
  /** brief 设定面板(风格/目的/比例/时长/基调)。 */
  showBrief: boolean;
  /** 产品图上传面板。 */
  showProduct: boolean;
  /** 自由文案输入框(出分镜后由分镜网格取代)。 */
  showFreeText: boolean;
  /** 模板填空面板。 */
  showTemplateFill: boolean;
  /** 手动保存草稿按钮。 */
  showSaveDraft: boolean;
  /**
   * 备料台①目的行(BPWB-1 拆自旧 `showSettings={freeShell}`)。
   * 蓝图态不出:拍法被蓝图公式盖掉(formula_card_from_payload 优先于选卡器),
   * 摆一个会被无视的选卡器就是假控件。★不看 hasStoryboard:幕二展开备料台仍照旧显示。
   */
  showPurpose: boolean;
  /**
   * 备料台②格式行(比例/时长/风格)。仿拍档不出:时长完全由原片镜头结构决定、
   * 比例跟原片锁死 —— 页面另画一行只读事实替代。学套路时这三样是真的,照旧。
   */
  showFormat: boolean;
}

/**
 * 来源判定**优先级**:海报 > 模板 > 追爆蓝图 > 目的卡 > 自由。
 *
 * 为什么要显式定序:这几个信号来自不同通道(query / location.state / form),理论上能同时
 * 出现(比如带 `?fromPoster=` 的 URL 上再补一份 state)。波1前那串 if 里 `fromPoster`
 * 出现在每个否定式的最前面,等于**事实上**已经是最高优先级 —— 这里只是把它写明,
 * 顺序照旧。前两档是"外部已把内容备好"的来源(brief/产品/文案框一律不出),
 * 后三档共用同一套自由外壳,差别只在顶部来源条显示什么。
 */
export function resolveVideoSource(input: VideoSourceInput): VideoSourceUi {
  const { fromPoster, templateId, hasBlueprint, purpose, hasStoryboard, fidelityMode } = input;

  const kind: VideoSourceKind = fromPoster
    ? "poster"
    : templateId
      ? "template"
      : hasBlueprint
        ? "explosive"
        : purpose
          ? "purpose"
          : "free";

  // 「自由外壳」= 波1前 `!fromPoster && !templateId` 那三处的正面写法
  const freeShell = !fromPoster && !templateId;

  return {
    kind,
    showBrief: freeShell && !hasStoryboard,
    /**
     * ★与波1前的第二处**故意**差异(两态收敛批,2026-07-26):原条件不看 hasStoryboard,
     * 出分镜后页面级产品面板还挂在分镜网格下面 —— 但那时改图只影响「重新生成的首帧」,
     * 面板却长得跟创建期一模一样,谁也不知道这点。收进分镜工具条的「素材」位后,
     * 那句话就写在改动入口旁边;页面级面板出分镜后退场。
     */
    showProduct: freeShell && !hasStoryboard,
    showFreeText: freeShell && !hasStoryboard,
    // 模板填空只在模板详情到手且未出分镜时出(页面侧再 && tpl,拉取中不闪空面板)
    showTemplateFill: kind === "template" && !hasStoryboard,
    /**
     * ★这一档是**故意**与波1前差一点的唯一一处:原条件是 `!fromPoster && !tpl`,
     * 用的是异步拉到的模板详情 `tpl` 而不是 `templateId`。于是模板流刚进页、详情还在路上
     * 那几百毫秒里,「保存草稿」会先冒出来再消失 —— 闪一下的按钮。
     * 改看 `templateId`(同页其余三处的判据)后不再闪。意图没变:模板流不给手动存草稿。
     */
    showSaveDraft: freeShell,
    // BPWB-1:两开关拆自页面侧旧 `showSettings={freeShell}`,故意不叠 hasStoryboard
    // (幕二展开备料台时它们本来就显示,收窄会把用户正在用的控件藏掉)。
    showPurpose: freeShell && kind !== "explosive",
    showFormat: freeShell && !(kind === "explosive" && fidelityMode === "copy"),
  };
}
